import React, { useMemo, useState } from 'react';
import { useAuth } from '../../AuthContext';
import {
  createEntity, updateEntity, useEntityList, useEntityDoc, useTenant,
  friendlyError, roleSatisfies,
} from '../../data/entities';
import {
  PURPOSES, PURPOSE_LABELS, RESOURCES, RESOURCE_LABELS, cleanRequest, roundUsd,
} from '../../data/budgetRequest';
import { apiPost } from '../../utils/backendApi';
import { decideBudgetRequest } from '../../data/policy';

// Members ask an org admin to raise a monthly alert limit. Admins decide
// through the policy API (shared/budgetRequest.js applyGrant runs there) so
// a second click cannot add the dollars twice and the raised limit is a
// restorable policy revision. Spend figures stay on the Costs card.
// A 'credits' request asks for prepaid model credits instead: approving
// records the decision and opens the Stripe top-up for the granted amount
// (the wallet is only ever credited by the billing webhook).

const fmt = (v) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', declined: 'Declined', cancelled: 'Cancelled' };

function Field({ label, children }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span className="cs-help" style={{ margin: 0 }}>{label}</span>
      {children}
    </label>
  );
}

export default function BudgetRequests({ org, myRole, appId = null, app = null }) {
  const { user } = useAuth();
  const tenant = useTenant();
  const isAdmin = myRole === 'admin' || myRole === 'owner';
  const canSee = roleSatisfies(myRole, 'member');
  const period = new Date().toISOString().slice(0, 7);
  const { docs: requests, error: listErr } = useEntityList('budgetRequest', { org: org.id }, { enabled: canSee, workspace: null });
  const { docs: apps } = useEntityList('appInstance', { org: org.id }, { enabled: canSee, workspace: null });
  const { doc: rollup } = useEntityDoc('orgCost', { org: org.id }, isAdmin ? period : null);

  const [purpose, setPurpose] = useState('upgrade');
  const [chosenApp, setChosenApp] = useState(appId || '');
  const [pipelineId, setPipelineId] = useState('');
  const [amount, setAmount] = useState('');
  const [resources, setResources] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [grants, setGrants] = useState({});
  const [decisionNotes, setDecisionNotes] = useState({});
  const [busyId, setBusyId] = useState('');

  const appName = useMemo(() => {
    const m = {};
    for (const a of apps || []) m[a.id] = a.name || a.slug || a.id;
    return m;
  }, [apps]);

  const targetId = appId || chosenApp;
  const targetApp = (apps || []).find((a) => a.id === targetId) || (app?.id === targetId ? app : null);
  const pipeWs = targetApp?.workspaceId || (!targetId ? tenant.workspace : null);
  const wantsPipeline = purpose === 'workflow' || purpose === 'job';
  const { docs: pipelines } = useEntityList(
    'pipeline',
    { org: org.id, workspace: pipeWs || '_' },
    { enabled: canSee && wantsPipeline && !!pipeWs, orderBy: 'name', dir: 'asc' },
  );

  if (!canSee) return null;

  const rows = (requests || [])
    .filter((r) => !appId || r.appId === appId)
    .slice()
    .sort((a, b) => {
      const pending = (b.status === 'pending') - (a.status === 'pending');
      if (pending) return pending;
      return (b.createdAt || 0) - (a.createdAt || 0);
    })
    .slice(0, 20);

  const toggleResource = (key) => {
    setResources((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const submit = async () => {
    setErr('');
    if (!user?.email) {
      setErr('Sign in with an email address to request a budget.');
      return;
    }
    const cleaned = cleanRequest({
      purpose, amountUsd: amount, resources, note, appId: targetId, pipelineId,
    });
    if (cleaned.error) {
      setErr(cleaned.error);
      return;
    }
    setSaving(true);
    try {
      const value = cleaned.value;
      await createEntity('budgetRequest', { org: org.id }, {
        workspaceId: targetApp?.workspaceId ?? null,
        purpose: value.purpose,
        amountUsd: value.amountUsd,
        resources: value.resources,
        note: value.note,
        ...(value.appId ? { appId: value.appId } : {}),
        ...(value.pipelineId ? { pipelineId: value.pipelineId } : {}),
        status: 'pending',
        notify: 'queued',
        createdBy: user.uid,
        requesterEmail: user.email,
      });
      setAmount('');
      setNote('');
      setResources([]);
      setPipelineId('');
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (req, status) => {
    const decisionNote = String(decisionNotes[req.id] || '').trim();
    if (status === 'declined' && !decisionNote) {
      setErr('Add a note so they know why this was declined.');
      return;
    }
    if (!user?.uid) {
      setErr('Sign in again to decide this request.');
      return;
    }
    const grantedUsd = roundUsd(grants[req.id] ?? req.amountUsd);
    setBusyId(req.id);
    setErr('');
    try {
      // Decided server-side (api/policyApi.js): the grant lands on
      // org.costLimits as a numbered policy revision + audit row, and the
      // rules no longer let the browser edit costLimits directly.
      const out = await decideBudgetRequest(org.id, req.id, { status, decisionNote, grantedUsd });
      if (out?.checkout) {
        const pay = await apiPost('/billing/credits/checkout', { orgId: org.id, usd: grantedUsd });
        if (pay?.url) window.location.assign(pay.url);
      }
    } catch (e) {
      setErr(e?.message && !e.code ? e.message : friendlyError(e));
    } finally {
      setBusyId('');
    }
  };

  const cancel = async (req) => {
    setBusyId(req.id);
    setErr('');
    try {
      await updateEntity('budgetRequest', { org: org.id }, req.id, { status: 'cancelled' });
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusyId('');
    }
  };

  const limitFor = (id) => (id ? org.costLimits?.apps?.[id] : org.costLimits?.monthlyUsd);
  const mtdFor = (id) => (id ? rollup?.byApp?.[id]?.mtdUsd : rollup?.mtdUsd);

  return (
    <section style={{ marginTop: appId ? 0 : 28 }}>
      {!appId && <h2 style={{ margin: 0 }}>Budget requests</h2>}
      <p className="cs-help">
        Ask an admin to raise the monthly budget. Approving adds the amount to that limit.
        Crossing it still only emails admins — nothing is paused. GPU and heavy compute still follow the plan.
      </p>
      <div className="cs-card" style={{ padding: 14, display: 'grid', gap: 10, maxWidth: 520 }}>
        {appId ? (
          <p className="cs-help" style={{ margin: 0 }}>{appName[appId] || app?.name || 'This app'}</p>
        ) : (
          <Field label="App">
            <select className="cs-input" value={chosenApp} onChange={(e) => { setChosenApp(e.target.value); setPipelineId(''); }}>
              <option value="">Organization monthly limit</option>
              {(apps || []).map((a) => <option key={a.id} value={a.id}>{a.name || a.slug}</option>)}
            </select>
          </Field>
        )}
        <Field label="What this is for">
          <select className="cs-input" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            {PURPOSES.map((p) => <option key={p} value={p}>{PURPOSE_LABELS[p]}</option>)}
          </select>
        </Field>
        {wantsPipeline && (
          pipeWs ? (
            <Field label="Workflow (optional)">
              <select className="cs-input" value={pipelineId} onChange={(e) => setPipelineId(e.target.value)}>
                <option value="">None</option>
                {(pipelines || []).map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
              </select>
            </Field>
          ) : (
            <p className="cs-help" style={{ margin: 0 }}>This app is not in a workspace, so a workflow cannot be attached.</p>
          )
        )}
        <Field label="Amount (USD)">
          <input className="cs-input" type="number" min="1" step="1" value={amount} placeholder="e.g. 500" onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {RESOURCES.map((key) => (
            <label key={key} className="cs-help" style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
              <input type="checkbox" checked={resources.includes(key)} onChange={() => toggleResource(key)} />
              {RESOURCE_LABELS[key]}
            </label>
          ))}
        </div>
        <Field label="Why you need it">
          <textarea className="cs-input" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div>
          <button type="button" className="cs-btn cs-btn-primary" disabled={saving} onClick={submit}>{saving ? 'Sending…' : 'Request'}</button>
        </div>
      </div>

      {listErr && <div className="cs-form-error" style={{ marginTop: 10 }}>{listErr}</div>}
      {err && <div className="cs-form-error" style={{ marginTop: 10 }}>{err}</div>}

      <div style={{ display: 'grid', gap: 10, marginTop: 14, maxWidth: 640 }}>
        {requests && !rows.length && <div className="cs-empty">No budget requests yet.</div>}
        {rows.map((req) => {
          const mine = req.createdBy === user?.uid;
          const busy = busyId === req.id;
          return (
            <div key={req.id} className="cs-card" style={{ padding: 14, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <strong>
                  {fmt(req.amountUsd)} · {PURPOSE_LABELS[req.purpose] || req.purpose}
                  {' · '}{req.appId ? (appName[req.appId] || req.appId) : 'Organization'}
                </strong>
                <span className={`ap-badge ${req.status === 'approved' ? 'is-ok' : ''}`}>{STATUS_LABEL[req.status] || req.status}</span>
              </div>
              <div className="cs-help" style={{ margin: 0 }}>
                {req.requesterEmail}
                {(req.resources || []).length > 0 && <> · {(req.resources || []).map((r) => RESOURCE_LABELS[r] || r).join(', ')}</>}
                {req.status === 'approved' && req.grantedUsd != null && <> · granted {fmt(req.grantedUsd)}</>}
              </div>
              {req.note && <p style={{ margin: 0 }}>{req.note}</p>}
              {req.decisionNote && <p className="cs-help" style={{ margin: 0 }}>{req.decisionNote}</p>}
              {isAdmin && req.status === 'pending' && (
                <div className="cs-help" style={{ margin: 0 }}>
                  {req.purpose === 'credits' ? (
                    <>Approving opens the prepaid-credits checkout for the amount below{rollup?.credits ? <> · {fmt(rollup.credits.remainingUsd)} of model credit left this month</> : null}</>
                  ) : (
                    <>
                      {limitFor(req.appId) > 0 ? <>Limit {fmt(limitFor(req.appId))}</> : 'No monthly limit yet — approving sets one'}
                      {rollup && <> · month to date {fmt(mtdFor(req.appId) || 0)}</>}
                    </>
                  )}
                </div>
              )}
              {isAdmin && req.status === 'pending' && (
                <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
                  <Field label={req.purpose === 'credits' ? 'Credits to buy (USD)' : 'Amount to add (USD)'}>
                    <input
                      className="cs-input"
                      type="number"
                      min="1"
                      step="1"
                      value={grants[req.id] ?? req.amountUsd}
                      onChange={(e) => setGrants({ ...grants, [req.id]: e.target.value })}
                    />
                  </Field>
                  <Field label="Note to the requester">
                    <input
                      className="cs-input"
                      value={decisionNotes[req.id] || ''}
                      onChange={(e) => setDecisionNotes({ ...decisionNotes, [req.id]: e.target.value })}
                    />
                  </Field>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="cs-btn cs-btn-primary" disabled={busy} onClick={() => decide(req, 'approved')} data-agent-noclick>{busy ? 'Saving…' : 'Approve'}</button>
                    <button type="button" className="cs-btn" disabled={busy} onClick={() => decide(req, 'declined')} data-agent-noclick>Decline</button>
                  </div>
                </div>
              )}
              {mine && req.status === 'pending' && !isAdmin && (
                <div>
                  <button type="button" className="cs-btn" disabled={busy} onClick={() => cancel(req)} data-agent-noclick>{busy ? 'Cancelling…' : 'Cancel request'}</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
